import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { money, isValidPhone } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function OrderFormDialog({ open, onOpenChange, onCreated }) {
  const [communes, setCommunes] = useState([]);
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState({ free_delivery_threshold: 50 });
  const [zones, setZones] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(blank());

  function blank() {
    return {
      client_name: "", whatsapp: "", phone2: "", address: "", reference: "",
      commune_id: "", quartier: "", collect_date: "", collect_time: "", comment: "",
      discount: 0, waive_delivery: false, items: [],
    };
  }

  useEffect(() => {
    if (open) {
      setForm(blank());
      setError("");
      Promise.all([
        api.get("/communes"), api.get("/services?active_only=true"),
        api.get("/settings"), api.get("/zones"),
      ]).then(([c, s, st, z]) => {
        setCommunes(c.data); setServices(s.data); setSettings(st.data); setZones(z.data);
      });
    }
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { _uid: crypto.randomUUID(), service_id: "", name: "", billing_mode: "piece", unit_price: 0, quantity: 1, weight: 0 }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updItem = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));

  const pickService = (i, sid) => {
    const svc = services.find((s) => s.id === sid);
    if (svc) updItem(i, { service_id: svc.id, name: svc.name, billing_mode: svc.billing_mode, unit_price: svc.unit_price });
  };

  const itemSub = (it) => it.billing_mode === "kilo" ? (Number(it.weight) || 0) * (Number(it.unit_price) || 0) : (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
  const serviceTotal = form.items.reduce((a, it) => a + itemSub(it), 0);
  const zone = zones.find((z) => (z.commune_ids || []).includes(form.commune_id));
  const baseFee = zone ? zone.delivery_fee : 0;
  const freeThreshold = settings.free_delivery_threshold || 0;
  const effFee = form.waive_delivery ? 0 : (freeThreshold && serviceTotal >= freeThreshold ? 0 : baseFee);
  const total = Math.max(serviceTotal + effFee - (Number(form.discount) || 0), 0);

  const submit = async () => {
    setError("");
    if (!form.client_name || !form.whatsapp || !form.commune_id) {
      setError("Nom, WhatsApp et commune sont obligatoires.");
      return;
    }
    if (!isValidPhone(form.whatsapp)) {
      setError("Numéro WhatsApp invalide. Format attendu : +243XXXXXXXXX");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        discount: Number(form.discount) || 0,
        items: form.items.map((it) => ({
          service_id: it.service_id || null, name: it.name, billing_mode: it.billing_mode,
          unit_price: Number(it.unit_price) || 0, quantity: Number(it.quantity) || 0, weight: Number(it.weight) || 0,
        })),
      };
      const { data } = await api.post("/orders", payload);
      toast.success(`Commande ${data.order_number} créée`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      setError(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Nouvelle commande</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Informations client</h4>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Nom complet *</Label><Input data-testid="of-name" value={form.client_name} onChange={set("client_name")} className="mt-1" /></div>
              <div>
                <Label>Numéro WhatsApp *</Label>
                <Input data-testid="of-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} placeholder="+243XXXXXXXXX" className={`mt-1 ${form.whatsapp && !isValidPhone(form.whatsapp) ? "border-red-400 ring-1 ring-red-300" : ""}`} />
                <p className={`text-xs mt-1 ${form.whatsapp && !isValidPhone(form.whatsapp) ? "text-red-600" : "text-slate-400"}`}>Format : +243 suivi de 9 chiffres (ex. +243812345678)</p>
              </div>
              <div><Label>Téléphone secondaire</Label><Input data-testid="of-phone2" value={form.phone2} onChange={set("phone2")} className="mt-1" /></div>
              <div className="sm:col-span-2"><Label>Adresse complète *</Label><Input data-testid="of-address" value={form.address} onChange={set("address")} className="mt-1" /></div>
              <div><Label>Référence *</Label><Input data-testid="of-reference" value={form.reference} onChange={set("reference")} className="mt-1" /></div>
              <div>
                <Label>Commune *</Label>
                <Select value={form.commune_id} onValueChange={set("commune_id")}>
                  <SelectTrigger data-testid="of-commune" className="mt-1"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{communes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Quartier</Label><Input data-testid="of-quartier" value={form.quartier} onChange={set("quartier")} className="mt-1" /></div>
            </div>
            {zone && <p className="text-xs text-slate-500 mt-2">Zone : <b>{zone.name}</b> — Frais {money(baseFee)}</p>}
          </div>

          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Collecte</h4>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label>Date</Label><Input data-testid="of-collect-date" type="date" value={form.collect_date} onChange={set("collect_date")} className="mt-1" /></div>
              <div><Label>Heure</Label><Input data-testid="of-collect-time" type="time" value={form.collect_time} onChange={set("collect_time")} className="mt-1" /></div>
              <div><Label>Remise ($)</Label><Input data-testid="of-discount" type="number" value={form.discount} onChange={set("discount")} className="mt-1" /></div>
            </div>
            <div className="mt-3"><Label>Commentaire</Label><Textarea data-testid="of-comment" value={form.comment} onChange={set("comment")} className="mt-1" rows={2} /></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-slate-700">Services</h4>
              <Button data-testid="of-add-item" size="sm" variant="outline" onClick={addItem}><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={it._uid || i} className="grid grid-cols-12 gap-2 items-end bg-slate-50 rounded-xl p-2">
                  <div className="col-span-4">
                    <Label className="text-xs">Service</Label>
                    <Select value={it.service_id} onValueChange={(v) => pickService(i, v)}>
                      <SelectTrigger data-testid={`of-item-service-${i}`} className="mt-1 h-9"><SelectValue placeholder="Service" /></SelectTrigger>
                      <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.billing_mode === "kilo" ? "kg" : "pièce"})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label className="text-xs">PU</Label><Input className="mt-1 h-9" type="number" value={it.unit_price} onChange={(e) => updItem(i, { unit_price: e.target.value })} /></div>
                  {it.billing_mode === "kilo" ? (
                    <div className="col-span-2"><Label className="text-xs">Poids kg</Label><Input data-testid={`of-item-weight-${i}`} className="mt-1 h-9" type="number" value={it.weight} onChange={(e) => updItem(i, { weight: e.target.value })} /></div>
                  ) : (
                    <div className="col-span-2"><Label className="text-xs">Qté</Label><Input data-testid={`of-item-qty-${i}`} className="mt-1 h-9" type="number" value={it.quantity} onChange={(e) => updItem(i, { quantity: e.target.value })} /></div>
                  )}
                  <div className="col-span-3 text-right text-sm font-semibold pb-2">{money(itemSub(it))}</div>
                  <div className="col-span-1 pb-1"><button data-testid={`of-item-remove-${i}`} onClick={() => removeItem(i)} className="text-red-500 p-1"><Trash2 className="w-4 h-4" /></button></div>
                </div>
              ))}
              {form.items.length === 0 && <p className="text-sm text-slate-400 text-center py-3">Aucun service (peut être complété à la collecte)</p>}
            </div>
          </div>

          <div className="flex items-center justify-between bg-slate-50 border rounded-xl px-4 py-3">
            <div>
              <Label className="text-sm font-medium">Dispenser des frais de livraison</Label>
              <p className="text-xs text-slate-400 mt-0.5">Pour les clients qui apportent / récupèrent eux-mêmes leurs vêtements</p>
            </div>
            <Switch data-testid="of-waive-delivery" checked={form.waive_delivery} onCheckedChange={(v) => setForm((f) => ({ ...f, waive_delivery: v }))} />
          </div>

          <div className="bg-brand-50 rounded-xl p-4 space-y-1 text-sm">
            <div className="flex justify-between"><span>Frais de service</span><b>{money(serviceTotal)}</b></div>
            <div className="flex justify-between"><span>Frais de livraison</span><b>{money(effFee)}</b>{form.waive_delivery ? <span className="text-emerald-600 text-xs ml-1">(dispensé)</span> : (effFee === 0 && baseFee > 0 && <span className="text-emerald-600 text-xs ml-1">(gratuit)</span>)}</div>
            <div className="flex justify-between"><span>Remise</span><b>-{money(form.discount || 0)}</b></div>
            <div className="flex justify-between text-base pt-1 border-t border-brand-200 mt-1"><span className="font-semibold">Total</span><b data-testid="of-total" className="text-brand-700">{money(total)}</b></div>
          </div>

          {error && <p data-testid="of-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button data-testid="of-submit" onClick={submit} disabled={saving} className="bg-brand-600 hover:bg-brand-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer la commande"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
